import { v4 as uuidv4 } from 'uuid';
import { proxy, subscribe } from 'valtio';

import dayjs, { Dayjs } from '@/dayjs';

import { GetBookingsExpandedQuery } from '@/graphql/GetBookingsExpanded.generated';

import { AssetSelectionItem, clearAssetSelection } from './assetSelection';
import { DateRange, setDateRange } from './daterange';
import { EnergyUsageDataKey } from './energyUsage';
import { OperatingPhase } from './operatingPhase';
import { ReeferAgesFilter, unselectAllReeferAges } from './reeferAges';
import {
  RefrigerationUnitModelsFilter,
  unselectAllRefrigerationUnitModels,
} from './refrigerationUnitModels';
import { TemperaturesFilter, unselectAllTemperatures } from './temperatures';
import { TradelanesFilter, unselectAllTradelanes } from './tradelanes';
import { Trip } from './trips';
import { ViewMode, ViewModeUnit } from './viewMode';

export type Message = {
  role: 'user' | 'ai';
  text: string;
  timestamp: number;
};
interface EmissionsDashboardStore {
  // filters have been fetched
  ready: boolean;

  // view mode
  viewMode: ViewMode;
  viewModeUnit: ViewModeUnit;
  viewModeDataKey: string;
  isAveragePowerMode: boolean;
  isCO2eMode: boolean;

  operatingPhase: OperatingPhase;
  isStableOperatingPhase: boolean;
  isTransitionOperatingPhase: boolean;
  isAllOperatingPhase: boolean;

  // daterange from/to filters
  daterange: DateRange;

  // temperatures filters
  temperatures: TemperaturesFilter[];
  selectedTemperatures: TemperaturesFilter[];

  // Reefer ages filters
  reeferAges: ReeferAgesFilter[];
  selectedReeferAges: ReeferAgesFilter[];

  // tradelanes filter
  tradelanes: TradelanesFilter[];
  selectedTradelanes: TradelanesFilter[];

  // reefer models filter
  refrigerationUnitModels: RefrigerationUnitModelsFilter[];
  selectedRefrigerationUnitModels: RefrigerationUnitModelsFilter[];

  // conversation id
  conversationId: string;

  // global static data
  globals: {
    minDate: Dayjs;
    etsSpotPricePerTon: number;
    emissionsReference: {
      energy: number | null;
      co2e: number | null;
    };
  };

  // indicators
  indicators: {
    reeferCount: number | null;
    etsInEuro: number | null;
    totalEnergy: number | null;
    totalCo2e: number | null;
  };

  // data fetched from backend
  assetIds: string[];
  assetIdsByModel: {
    [modelName: string]: string[];
  };
  assetsModel: {
    [assetId: string]: string;
  };
  trips: Trip[];

  bookings: GetBookingsExpandedQuery['container_insights_bookings_expanded'];

  // assets
  selectedAssets: AssetSelectionItem[];

  // chat messages
  chatMessages: Message[];
}

let store = proxy<EmissionsDashboardStore>({
  get ready() {
    return (
      this.refrigerationUnitModels.length > 0 &&
      this.reeferAges.length > 0 &&
      this.temperatures.length > 0 &&
      this.tradelanes.length > 0
    );
  },

  // view mode
  viewMode: ViewMode.AVERAGE_POWER,
  get viewModeUnit() {
    const indexOf = Object.values(ViewMode).indexOf(this.viewMode);
    const key = Object.keys(ViewMode)[indexOf];
    return ViewModeUnit[key as keyof typeof ViewModeUnit];
  },
  get isAveragePowerMode() {
    return this.viewMode === ViewMode.AVERAGE_POWER;
  },
  get isCO2eMode() {
    return this.viewMode === ViewMode.CO2E;
  },

  operatingPhase: OperatingPhase.ALL,
  get isStableOperatingPhase() {
    return this.operatingPhase === OperatingPhase.STABLE;
  },
  get isTransitionOperatingPhase() {
    return this.operatingPhase === OperatingPhase.TRANSITION;
  },
  get isAllOperatingPhase() {
    return this.operatingPhase === OperatingPhase.ALL;
  },

  get viewModeDataKey(): EnergyUsageDataKey {
    let dataKey: EnergyUsageDataKey = this.isAveragePowerMode
      ? 'active_power_kW'
      : 'co2eq_kg_per_hour';

    switch (this.operatingPhase) {
      case OperatingPhase.STABLE:
        dataKey += '_stable';
        break;
      case OperatingPhase.TRANSITION:
        dataKey += '_transition';
        break;
      default:
    }

    return dataKey as EnergyUsageDataKey;
  },

  // daterange filter
  daterange: {
    from: dayjs().startOf('month').subtract(6, 'month'),
    to: dayjs().endOf('month'),
  },

  // temperatures filter
  temperatures: [],
  get selectedTemperatures() {
    return this.temperatures.filter(
      (temperature: TemperaturesFilter) => temperature.selected,
    );
  },

  // Reefer ages filter
  reeferAges: [],
  get selectedReeferAges() {
    return this.reeferAges.filter((age: ReeferAgesFilter) => age.selected);
  },

  // tradelanes filter
  tradelanes: [],
  get selectedTradelanes() {
    return this.tradelanes.filter(
      (tradelane: TradelanesFilter) => tradelane.selected,
    );
  },

  // models filter
  refrigerationUnitModels: [],
  get selectedRefrigerationUnitModels() {
    return this.refrigerationUnitModels.filter(
      (model: RefrigerationUnitModelsFilter) => model.selected,
    );
  },

  // global static data
  globals: {
    minDate: dayjs('2020-01-01'),
    etsSpotPricePerTon: 70,
    emissionsReference: {
      energy: null,
      co2e: null,
    },
  },

  // metrics indicators
  indicators: {
    reeferCount: null,
    etsInEuro: 0,
    totalEnergy: 0,
    totalCo2e: 0,
  },

  // data fetched from backend
  assetIds: [],
  assetIdsByModel: {},
  assetsModel: {},
  trips: [],

  bookings: [],

  selectedAssets: [],

  conversationId: '',

  chatMessages: [],
});
export const setConversationId = () => {
  store.conversationId = uuidv4();
  store.chatMessages = [];
};

const resetFilters = (isMobileDevice: boolean) => {
  setDateRange(
    dayjs().startOf('month').subtract(6, 'month'),
    dayjs().endOf('month'),
  );
  unselectAllTemperatures();
  unselectAllReeferAges();
  unselectAllTradelanes();
  // only clear models selection on mobile device
  if (isMobileDevice) {
    unselectAllRefrigerationUnitModels();
    clearAssetSelection();
  }
};

subscribe(store, () => {
  if (import.meta.hot) {
    import.meta.hot.data.store = store;
  }
});

if (import.meta.hot) {
  import.meta.hot.on('vite:afterUpdate', () => {
    store = proxy(import.meta.hot?.data.store);
  });
}

export { store, ViewMode, resetFilters };
export type { EmissionsDashboardStore };
